// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useTaskQuery } from "@/api/hooks/use-tasks";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Button } from "@/components/ui/button";
import { TaskActionsMenu } from "@/features/tasks/task-actions-menu";
import { TaskKindBadge } from "@/features/tasks/task-kind-badge";
import { ToggleTaskActiveDialog } from "@/features/tasks/toggle-task-active-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/tasks/$subtype")({
  validateSearch: z.object({ t: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.tasks}>
      <TaskDetailLayout />
    </RequirePermission>
  ),
});

/**
 * Chrome shared by the task's read page (`/tasks/$subtype`) and its edit page
 * (`/tasks/$subtype/edit`): the identity pill row, the actions menu and its dialog.
 * Keeping it in the layout means the header stays mounted when moving between the two.
 */
function TaskDetailLayout() {
  const { subtype } = Route.useParams();
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const matchRoute = useMatchRoute();
  const { data: task, isLoading, isError } = useTaskQuery(subtype);

  // A built-in type has no definition to configure, so it never gets an edit page. The button that
  // opens it is hidden once we're there — the task-name breadcrumb is the way back.
  const isBuiltIn = task?.kind === "builtIn";
  const isEditPage = !!matchRoute({ to: "/tasks/$subtype/edit" });
  const canEdit = hasPermission(PAGE_PERMISSIONS.tasksEdit) && !isBuiltIn;
  const displayName = task?.fullName ?? subtype;

  const breadcrumbs = useMemo(
    () =>
      isEditPage
        ? [
            { label: t("tasks.title"), to: "/tasks" },
            { label: displayName, to: `/tasks/${subtype}`, bold: true },
          ]
        : [{ label: t("tasks.title"), to: "/tasks" }],
    [t, isEditPage, displayName, subtype],
  );

  const [toggleOpen, setToggleOpen] = useState(false);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !task) {
    return (
      <div>
        <PageHeader title={subtype} breadcrumbs={[{ label: t("tasks.title"), to: "/tasks" }]} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditPage ? t("buttons.edit") : task.fullName}
        subtitle={
          <>
            {task.customTaskDefinitionId != null && <IdPill id={task.customTaskDefinitionId} />}
            <KeyChip label={task.key} />
            <TaskKindBadge kind={task.kind} />
            <StatusIndicator active={task.isActive} />
            {!isBuiltIn && (
              <div className="ml-auto -my-1.5 flex items-center gap-2">
                {/* 30px rather than the `sm` 32px — sits a touch tighter in the pill row */}
                {canEdit && !isEditPage && (
                  <Button asChild size="sm" className="h-7.5">
                    <Link to="/tasks/$subtype/edit" params={{ subtype }} search={{}}>
                      <Pencil className="h-4 w-4" />
                      {t("buttons.edit")}
                    </Link>
                  </Button>
                )}
                <TaskActionsMenu isActive={task.isActive} onToggleActive={() => setToggleOpen(true)} />
              </div>
            )}
          </>
        }
        breadcrumbs={breadcrumbs}
      />

      <Outlet />

      <ToggleTaskActiveDialog
        task={toggleOpen ? { subtype: task.key, name: task.fullName, isActive: task.isActive } : null}
        onClose={() => setToggleOpen(false)}
      />
    </div>
  );
}
